import {AfterViewInit, Inject, Injectable, InjectionToken, Optional, signal} from "@angular/core";
import {MatDialogRef} from "@angular/material/dialog";
import {AgendaelementId, MøteId} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {EksisterendeDokumentFunksjon, OpplastetDokument, Skyfil, SkyfilOpplasting, ValgteSkyfiler} from "../SkyfilOpplasting";
import {FilMedOpplastingsstatus, OpplastingsstatusSkyfilerKomponent, OpplastingsstatusSkyfilerModus, ValgtFil} from "../komponenter/opplastingsstatus-skyfiler/opplastingsstatus-skyfiler";
import {Subscription} from "rxjs";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {erIkkeTomListe} from "felles-ng2/støttefunksjoner/lister";
import {Debugger} from "debug";

/**
 * Abstrakt basisklasse for FilvelgerDialog.
 *
 * Merk at angular krever at abstrakte klasser som benytter angular konstruksjoner som feks signal
 * gjøre 'Injectable', derfor er denne klassen annotert med @Injectable() samt at
 * konstruktørens argumenter er annotert med '@Optional() @Inject(INGEN_DATA)'
 */
@Injectable()
export abstract class BasisFilvelgerDialog<SKYFIL extends Skyfil, SPESIFIKASJON> implements AfterViewInit {

    private sakId: AgendaelementId;
    private møteId: MøteId
    private eksisterendeDokument: EksisterendeDokumentFunksjon;

    modus = signal<'velgFiler' | 'listeValgte'>('velgFiler');
    valgteFilerMedOpplastingsstatus = signal<FilMedOpplastingsstatus[]>([]);
    dokumenterMedNyVersjon = signal<ValgtFil[]>([]);
    opplastingsstatusModus = signal<OpplastingsstatusSkyfilerModus>('lasterOpp');

    private valgteFiler: ValgteSkyfiler<SKYFIL> | null = null;
    private pågåendeOpplasting: Subscription | null = null;

    /** Se dokumentasjon på klassenivå */
    constructor(@Optional() @Inject(INGEN_DATA) parametre: DialogParametre,
                @Optional() @Inject(INGEN_DATA) protected dialogRef: MatDialogRef<any>,
                @Optional() @Inject(INGEN_DATA) private skyfilOpplasting: SkyfilOpplasting<SPESIFIKASJON, SKYFIL>,
                @Optional() @Inject(INGEN_DATA) private debug: Debugger) {

        this.sakId = parametre.sakId;
        this.møteId = parametre.møteId;
        this.eksisterendeDokument = parametre.eksisterendeDokument;
    }

    startOpplasting() {
        this.opplastingsstatusModus.set('lasterOpp')
        this.lastOpp();
    }

    /** Bruker klikket på avbryt etter at filer har blitt valgt, opplasting av filer er antagelig pågående */
    avbrytLastOpp() {
        this.dialogRef.close();
        this.pågåendeOpplasting?.unsubscribe();
        return;
    }

    lukk() {
        this.dialogRef.close();
    }

    ngAfterViewInit(): void {
        this.velgFilerOgLastOpp(); //må kalles etter at html har blitt tegnet ut, derfor i ngAfterViewInit og ikke i konstruktør
    }

    // implementeres av subklasse

    protected abstract velgFiler(): Promise<ValgteSkyfiler<SKYFIL> | null>;

    // private hjelpemetoder

    private async velgFilerOgLastOpp() {
        //vis picker gui i iframen
        this.valgteFiler = await this.velgFiler();
        this.debug('visFilvelger valgte filer:', this.valgteFiler);

        //bruker avbrøt eller feil oppstod
        if (erNil(this.valgteFiler)) {
            this.lukk();
            return;
        }

        // sørger for at vår modal vises
        this.dialogRef.addPanelClass('vis');

        //vis liste med valgte filer, last opp
        this.modus.set('listeValgte');
        this.valgteFilerMedOpplastingsstatus.set(OpplastingsstatusSkyfilerKomponent.medOpplastetStatus(this.valgteFiler!.dokumenter));
        //dersom bruker har valgt dokumenter som allerede eksisterer i saken må bruker bekrefte at nye versjoner skal registreres
        const filerGruppert = this.skyfilOpplasting.grupperINyeOgOppdaterte(this.valgteFiler!, this.eksisterendeDokument);
        if (erIkkeTomListe(filerGruppert.oppdaterte)) {
            this.dokumenterMedNyVersjon.set(filerGruppert.oppdaterte.map(oppdatert => oppdatert.fil));
            this.opplastingsstatusModus.set('bekreftNyeVersjoner')
            return;
        }

        this.startOpplasting();
    }

    private lastOpp() {
        this.debug('lastOpp', this.valgteFiler)
        this.pågåendeOpplasting = this.skyfilOpplasting.lastOpp(this.valgteFiler!, this.sakId, this.eksisterendeDokument)
          .pipe()
          .subscribe({
              next: dokument => this.dokumentRegistrert(dokument),
              complete: () => this.alleDokumenterRegistrert()
          });
    }

    /** Kalles når et valgt dokument har blitt lastet opp (dvs når server er ferdig med å hente dokumentet fra onedrive og lagre det i sp3 db.
     * Dersom opplasting feilet vil dokument.feil være ikke-nil. */
    private dokumentRegistrert(dokument: OpplastetDokument<SKYFIL>) {
        this.debug('lastOpp: dokument lastet opp til sak', dokument)

        this.valgteFilerMedOpplastingsstatus.set(
          OpplastingsstatusSkyfilerKomponent.oppdaterOpplastetStatus(dokument, this.valgteFilerMedOpplastingsstatus()));
    }

    /** kalles når alle valgte dokument har blitt lastet */
    private alleDokumenterRegistrert() {
        this.debug('lastOpp: alle dokumenter lastet opp')
        if (this.valgteFilerMedOpplastingsstatus().some(opplasting => opplasting.feilet)) {
            //minst én opplasting feilet, informer bruker og ikke lukk dialog før bruker velger å lukke
            this.opplastingsstatusModus.set('opplastingFeilet')
            return;
        }
        this.lukk();
    }
}

export interface DialogParametre {
    sakId: AgendaelementId;
    møteId: MøteId;
    eksisterendeDokument: EksisterendeDokumentFunksjon;
}

export declare const INGEN_DATA: InjectionToken<any>;
